import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import FileDropzone from "@/components/FileDropzone";
import { formatDate, formatBytes } from "@/lib/orderUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download, Trash2, Upload, ScrollText, FileText, Building2, Stethoscope, CheckCircle2 } from "lucide-react";

function ClientListinoRequest() {
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState(false);

  const request = async (position) => {
    setSending(position);
    try {
      await api.post("/listino/request", { position });
      setSent(true);
      toast.success("Richiesta inviata al laboratorio");
    } catch (e) {
      toast.error("Errore nell'invio della richiesta. Riprova.");
    } finally {
      setSending(null);
    }
  };

  return (
    <DashboardLayout title="Listino" subtitle="Richiedi il listino dedicato alla tua attività.">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
        <Card className="p-8 border-slate-200" data-testid="listino-request-card">
          {sent ? (
            <div className="text-center py-8" data-testid="listino-request-sent">
              <div className="h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mt-5">Richiesta inviata</h3>
              <p className="text-slate-500 mt-2">Abbiamo ricevuto la tua richiesta di listino. Ti ricontatteremo al più presto con le condizioni dedicate.</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                  <ScrollText className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">I nostri listini sono personalizzati</h3>
                  <p className="text-slate-500 mt-2 leading-relaxed">
                    Lavoriamo sia con <strong>studi dentistici</strong> sia con <strong>laboratori odontotecnici</strong>, con condizioni dedicate a ciascuna attività. Seleziona la tua posizione per richiedere il listino corretto: te lo invieremo il prima possibile.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <button
                  data-testid="request-listino-studio"
                  onClick={() => request("studio")}
                  disabled={!!sending}
                  className="text-left rounded-xl border border-slate-200 p-5 hover:border-teal-500 hover:shadow-md transition-all disabled:opacity-60"
                >
                  <div className="h-11 w-11 rounded-xl bg-teal-600 flex items-center justify-center mb-4">
                    <Stethoscope className="h-6 w-6 text-white" />
                  </div>
                  <p className="font-semibold text-slate-900">Sono uno Studio Dentistico</p>
                  <p className="text-sm text-slate-500 mt-1">Richiedi il listino per studi dentistici.</p>
                  {sending === "studio" && <Loader2 className="h-4 w-4 animate-spin text-teal-600 mt-3" />}
                </button>
                <button
                  data-testid="request-listino-laboratorio"
                  onClick={() => request("laboratorio")}
                  disabled={!!sending}
                  className="text-left rounded-xl border border-slate-200 p-5 hover:border-teal-500 hover:shadow-md transition-all disabled:opacity-60"
                >
                  <div className="h-11 w-11 rounded-xl bg-slate-800 flex items-center justify-center mb-4">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <p className="font-semibold text-slate-900">Sono un Laboratorio Odontotecnico</p>
                  <p className="text-sm text-slate-500 mt-1">Richiedi il listino per laboratori.</p>
                  {sending === "laboratorio" && <Loader2 className="h-4 w-4 animate-spin text-teal-600 mt-3" />}
                </button>
              </div>
            </>
          )}
        </Card>
      </motion.div>
    </DashboardLayout>
  );
}

export default function ListinoPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [desc, setDesc] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/listino");
      setItems(data);
    } catch (e) {
      toast.error("Errore nel caricamento del listino");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("description", desc);
        await api.post("/listino/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Listino caricato");
      setFiles([]);
      setDesc("");
      load();
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const download = async (id, filename) => {
    try {
      const res = await api.get(`/listino/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
    } catch (e) {
      toast.error("Errore nel download");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/listino/${id}`);
      toast.success("Voce eliminata");
      load();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  if (!isAdmin) return <ClientListinoRequest />;

  return (
    <DashboardLayout title="Listino" subtitle="Carica e gestisci i documenti del listino prezzi.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-6 border-slate-200">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-16">
                <ScrollText className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-4 font-medium text-slate-700">Nessun listino disponibile</p>
                <p className="text-sm text-slate-500">Richiedi info per avere il listino.</p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="listino-list">
                {items.map((it, i) => (
                  <motion.li key={it.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                        <FileText className="h-5 w-5 text-teal-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.filename}</p>
                        <p className="text-xs text-slate-400">{it.description ? `${it.description} · ` : ""}{formatBytes(it.size)} · {formatDate(it.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" data-testid="listino-download-button" onClick={() => download(it.id, it.filename)}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid="listino-delete-button" onClick={() => remove(it.id)} className="text-rose-500 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card className="p-6 border-slate-200 h-fit" data-testid="listino-upload-card">
          <h3 className="font-heading text-lg font-semibold mb-4">Carica listino</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Descrizione (opzionale)</Label>
              <Input data-testid="listino-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="es. Listino Studi Dentistici 2026" />
            </div>
            <FileDropzone files={files} setFiles={setFiles} disabled={uploading} />
            <Button data-testid="listino-upload-button" onClick={upload} disabled={uploading || files.length === 0} className="w-full bg-teal-600 hover:bg-teal-700">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
